/**
 * Portfolio scope-override toggle: "Show all customers" in the account menu.
 *
 * State lives in per-device storage (web: localStorage). When ON, an
 * `X-Scope-Override: all` request header is sent on every API call via the
 * shared API client's extra-headers provider. The backend honours the
 * header for sales_rep, csr and engineer roles only (R16); admin/manager are already
 * unscoped, pending stays empty regardless. See
 * `.claude/rules/security.md` "The portfolio rule" for the policy.
 *
 * This hook is platform-neutral. It reads/writes via the storage
 * capability port; the shell's implementation handles localStorage.
 */
import { useCallback, useEffect, useState } from "react";

import { usePlatformCapabilities } from "../capabilities/context";
// Imported directly from ./providers (not ./client) so registering a
// header provider doesn't pull client.ts — and its module-load read of
// VITE_API_BASE_URL — into the import graph at app boot. Critical for
// the e2e mock-mode build where no API URL is set; client.ts would
// throw and the whole app would refuse to render.
import { setExtraHeadersProvider } from "../data/api/providers";

export const SCOPE_OVERRIDE_STORAGE_KEY = "cis.scope_override";
export const SCOPE_OVERRIDE_HEADER = "X-Scope-Override";
const STORAGE_VALUE_ON = "all";

// Shared pub/sub so a page-local toggle (useScopeOverrideValue) and the
// account-menu toggle (useScopeOverride) stay in sync while both are
// mounted. Only useScopeOverride ever touches setExtraHeadersProvider —
// keeping that registration single-owner avoids one instance's unmount
// clobbering the header for another still-mounted instance.
const listeners = new Set<(next: boolean) => void>();

function notifyListeners(next: boolean): void {
  listeners.forEach((listener) => listener(next));
}

interface UseScopeOverrideOptions {
  /**
   * The current user's role. The toggle is visually meaningful only for
   * `sales_rep`, `csr` and `engineer` — for any other role the toggle is forced "off"
   * regardless of what's in storage. The header provider also returns no
   * header for those roles. This prevents a stale "on" state lingering
   * across role downgrades.
   */
  role: string | null | undefined;
}

interface UseScopeOverrideResult {
  /**
   * True iff the toggle is on AND the user's role can meaningfully use it.
   * Use this for rendering the menu item's checked state.
   */
  showAll: boolean;

  /**
   * Set the toggle on/off. Persists to storage; the header provider picks
   * up the new value on the next API call.
   */
  setShowAll: (next: boolean) => void;
}

/** Roles the scope-override toggle has any functional effect for — other
 * roles either bypass scoping already (admin/manager) or have no
 * portfolio at all (pending). Exported so every toggle UI (account menu,
 * page-local controls) gates visibility identically. */
export function isScopeOverrideRole(role: string | null | undefined): boolean {
  return role === "sales_rep" || role === "csr" || role === "engineer";
}

/**
 * Register the extra-headers provider for the scope-override header and
 * expose toggle state to UI consumers.
 *
 * Mount this once at the app root (after the capability + auth providers).
 * The hook returns the current state plus a setter that updates storage
 * and notifies any other mounted instance via a window-level event.
 */
export function useScopeOverride({
  role,
}: UseScopeOverrideOptions): UseScopeOverrideResult {
  const capabilities = usePlatformCapabilities();

  // Initial state: read from storage exactly once, then maintain in React state.
  const [storedOn, setStoredOn] = useState<boolean>(() => {
    return (
      capabilities.getStorageItem(SCOPE_OVERRIDE_STORAGE_KEY) ===
      STORAGE_VALUE_ON
    );
  });

  const roleCanUseOverride = isScopeOverrideRole(role);
  const effectiveOn = storedOn && roleCanUseOverride;

  // Register the extra-headers provider on mount. The provider reads the
  // latest `effectiveOn` via a ref-like pattern (closures over the latest
  // values via the effect dependency list). On unmount, reset to a no-op.
  useEffect(() => {
    setExtraHeadersProvider((): Record<string, string> =>
      effectiveOn ? { [SCOPE_OVERRIDE_HEADER]: "all" } : {},
    );
    return () => {
      setExtraHeadersProvider(() => ({}));
    };
  }, [effectiveOn]);

  // Pick up writes made via a page-local useScopeOverrideValue toggle
  // elsewhere on the page (e.g. CustomersPage) while this instance is
  // mounted, so the header this instance owns stays correct.
  useEffect(() => {
    listeners.add(setStoredOn);
    return () => {
      listeners.delete(setStoredOn);
    };
  }, []);

  const setShowAll = useCallback(
    (next: boolean) => {
      capabilities.setStorageItem(
        SCOPE_OVERRIDE_STORAGE_KEY,
        next ? STORAGE_VALUE_ON : null,
      );
      setStoredOn(next);
      notifyListeners(next);
    },
    [capabilities],
  );

  return { showAll: effectiveOn, setShowAll };
}

/**
 * Lightweight read+write access to the same scope-override toggle for a
 * secondary UI location (e.g. a page-local "My customers / All customers"
 * control on CustomersPage) — anywhere that isn't the single instance
 * responsible for owning the header-provider registration.
 *
 * Unlike `useScopeOverride`, this never calls `setExtraHeadersProvider` —
 * it only reads/writes storage and stays in sync with the owning instance
 * via the shared listener set, so mounting or unmounting it can never
 * clobber the header another mounted `useScopeOverride` instance set up.
 */
export function useScopeOverrideValue({
  role,
}: UseScopeOverrideOptions): UseScopeOverrideResult {
  const capabilities = usePlatformCapabilities();

  const [storedOn, setStoredOn] = useState<boolean>(() => {
    return (
      capabilities.getStorageItem(SCOPE_OVERRIDE_STORAGE_KEY) ===
      STORAGE_VALUE_ON
    );
  });

  useEffect(() => {
    listeners.add(setStoredOn);
    return () => {
      listeners.delete(setStoredOn);
    };
  }, []);

  const roleCanUseOverride = isScopeOverrideRole(role);
  const effectiveOn = storedOn && roleCanUseOverride;

  const setShowAll = useCallback(
    (next: boolean) => {
      capabilities.setStorageItem(
        SCOPE_OVERRIDE_STORAGE_KEY,
        next ? STORAGE_VALUE_ON : null,
      );
      setStoredOn(next);
      notifyListeners(next);
    },
    [capabilities],
  );

  return { showAll: effectiveOn, setShowAll };
}
